import React, { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withStyles, makeStyles, createStyles } from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import Pagination from "@material-ui/lab/Pagination";
import { Typography } from "@material-ui/core";
import {
  setTotalFiles,
  setAreaGroup,
  setCurrentPageNumber,
  setPageTopLoader,
} from "../../actions";
import { getJwtToken } from "../../api";

const useStyles = makeStyles((theme) =>
  createStyles({
    tableWrapper: {
      width: "100%",
    },
    tableWrapperSlim: {
      width: "89%",
    },
    table: {
      width: "100%",
    },
    tableHead: {
      height: 40,
      backgroundColor: "#fafafa",
    },
    tableRow: {
      height: 126,
      "&:hover": {
        backgroundColor: theme.palette.action.hover,
      },
    },
    tableRowNone: {
      height: 126,
    },
    noDataCell: {
      textAlign: "center",
      padding: theme.spacing(3),
      fontSize: 14,
      color: theme.palette.text.secondary,
    },
    pagination: {
      display: "flex",
      justifyContent: "center",
      marginTop: theme.spacing(4),
      marginBottom: theme.spacing(4),
    },
    tableBlock: {
      width: "100%",
    },
    tableBlockHeader: {
      display: "flex",
      justifyContent: "flex-end",
      alignItems: "center",
      marginBottom: 6, // テーブル枠との間隔
    },
    tableNote: {
      whiteSpace: "nowrap",
    },
    fileMeta: {
      marginLeft: theme.spacing(1),
      color: theme.palette.text.secondary,
      fontSize: 12,
      whiteSpace: "nowrap",
    },
  })
);

const normalizeAreaValue = (value) => {
  if (typeof value !== "string") return "";
  return value.replace(/（\d+件）$/, "").trim();
};

const normalizeSelectedAreas = (value) => {
  if (Array.isArray(value)) return value.map(normalizeAreaValue).filter(Boolean);
  if (typeof value === "string") return [normalizeAreaValue(value)].filter(Boolean);
  return [];
};

const DEFAULT_DOWNLOAD_LIMIT = 10;
const DOWNLOAD_API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/+$/, "");

const extractZipFileName = (row) => {
  if (row && row.fileName) return String(row.fileName);
  if (row && row.url) {
    try {
      const pathname = new URL(row.url).pathname;
      return decodeURIComponent(pathname.split("/").pop() || "");
    } catch (_error) {
      const parts = String(row.url).split("/");
      return decodeURIComponent(parts[parts.length - 1] || "");
    }
  }
  return "";
};

const getAuthHeaders = (tokenOverride = "") => {
  const idToken = tokenOverride || localStorage.getItem("idToken") || "";
  return {
    "Content-Type": "application/json",
    ...(idToken ? { Authorization: idToken } : {}),
  };
};

const fetchWithAuthRetry = async (url, init) => {
  let response = await fetch(url, init);

  if (response.status === 401) {
    const refreshedToken = await getJwtToken();
    response = await fetch(url, {
      ...init,
      headers: getAuthHeaders(refreshedToken),
    });
  }

  return response;
};

const getFreshAuthHeaders = async () => {
  try {
    const refreshedToken = await getJwtToken();
    return getAuthHeaders(refreshedToken);
  } catch (error) {
    console.warn("Failed to refresh idToken before download request:", error);
    return getAuthHeaders();
  }
};

const DownloadTable = ({
  setCurrentPageNumber,
  setPageTopLoader,
  open,
  currentPageNumber,
  setTotalFiles,
  setAreaGroup,
  filterDownload,
  onFileNameOptionsChange,
  onFilteredCountChange,
}) => {
  const classes = useStyles();
  const [tableData, setTableData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const activeRequestCountRef = useRef(0);
  const itemsPerPage = 10;

  const runWithPageTopLoader = useCallback(async (requestFn) => {
    activeRequestCountRef.current += 1;
    if (activeRequestCountRef.current === 1) {
      setPageTopLoader(true);
    }
    try {
      return await requestFn();
    } finally {
      activeRequestCountRef.current = Math.max(0, activeRequestCountRef.current - 1);
      if (activeRequestCountRef.current === 0) {
        setPageTopLoader(false);
      }
    }
  }, [setPageTopLoader]);

  const fetchDownloadListViaApi = async () => {
    if (!DOWNLOAD_API_BASE_URL) {
      throw new Error("REACT_APP_API_BASE_URL is not configured");
    }

    const response = await fetchWithAuthRetry(`${DOWNLOAD_API_BASE_URL}/download/list`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({ limit: 1000 }),
    });

    if (!response.ok) {
      throw new Error(`Failed to load download list API: ${response.status}`);
    }

    const payload = await response.json();
    const items = Array.isArray(payload?.items) ? payload.items : [];

    return items.map((item) => {
      const fileName = String(item.fileName || "");
      const normalizedDate = String(item.date || "");
      const area = String(item.area || "");
      const remaining = Number(item.remainingDownloadCount);
      const reportCount = Number(
        item.reportCount !== undefined ? item.reportCount : item.report_count
      );
      const imageCount = Number(
        item.imageCount !== undefined ? item.imageCount : item.image_count
      );
      const baseUrl = (process.env.REACT_APP_PICTREE_DATA_BASE_URL || "").replace(/\/+$/, "");
      const encodedFileName = encodeURIComponent(fileName);

      return {
        ...item,
        fileName,
        name: fileName.replace(/\.zip$/i, ""),
        date: normalizedDate,
        area,
        reportCount: Number.isFinite(reportCount) ? reportCount : undefined,
        imageCount: Number.isFinite(imageCount) ? imageCount : undefined,
        remainingDownloadCount: Number.isFinite(remaining) ? remaining : DEFAULT_DOWNLOAD_LIMIT,
        url: item.url || (baseUrl ? `${baseUrl}/tepco/download/${encodedFileName}` : ""),
        csvUrl:
          item.csvUrl ||
          (baseUrl && fileName
            ? `${baseUrl}/tepco/download/${encodeURIComponent(fileName.replace(/\.zip$/i, ".csv"))}`
            : ""),
      };
    });
  };

  // Redux に保存しているページ番号 → ローカル state に反映
  useEffect(() => {
    setCurrentPage(currentPageNumber || 1);
  }, [currentPageNumber]);

  // download.json を読み込む（初回のみ）
  useEffect(() => {
    const fetchDownloadList = async () => {
      try {
        const displayData = await runWithPageTopLoader(() => fetchDownloadListViaApi());
        setTableData(displayData);

        if (typeof onFileNameOptionsChange === "function") {
          const options = Array.from(
            new Set(
              displayData
                .flatMap((item) => String(item.fileName || "").split(/[\s._/-]+/))
                .map((token) => token.trim())
                .filter(Boolean)
            )
          ).sort((a, b) => a.localeCompare(b, "ja"));
          onFileNameOptionsChange(options);
        }

        // 全件数
        setTotalFiles(displayData.length);

        // 県域ごとの件数
        const areaCountMap = displayData.reduce((acc, item) => {
          if (!item.area) return acc;
          acc[item.area] = (acc[item.area] || 0) + 1;
          return acc;
        }, {});

        const areaOptions = [
          {
            value: "all",
            label: "全て",
            count: displayData.length,
          },
          ...Object.entries(areaCountMap).map(([area, count]) => ({
            value: area,
            label: `${area}（${count}件）`,
            count,
          })),
        ];

        setAreaGroup(areaOptions);
      } catch (err) {
        console.error("Error loading download list:", err);
        setTableData([]);
        if (typeof onFileNameOptionsChange === "function") {
          onFileNameOptionsChange([]);
        }
        setTotalFiles(0);
        setAreaGroup([
          {
            value: "all",
            label: "全て",
            count: 0,
          },
        ]);
      }
    };

    fetchDownloadList();
  }, [setTotalFiles, setAreaGroup, onFileNameOptionsChange, runWithPageTopLoader]);

  const handleDownload = (url) => {
    if (!url) return;

    const a = document.createElement("a");
    a.href = url;
    a.target = "_self";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    // RPA実行環境などで programmatic click が抑止される場合のフォールバック
    setTimeout(() => {
      try {
        window.location.assign(url);
      } catch (e) {
        console.error("Failed to navigate for download:", e);
      }
    }, 150);
  };

  const handleZipDownload = async (row) => {
    if (!row) return;

    try {
      if (!DOWNLOAD_API_BASE_URL) {
        throw new Error("REACT_APP_API_BASE_URL is not configured");
      }

      const fileName = extractZipFileName(row);
      const payload = {
        fileName,
        email: localStorage.getItem("email") || "",
        userId: localStorage.getItem("username") || "",
        userName: localStorage.getItem("username") || "",
      };
      const authHeaders = await getFreshAuthHeaders();

      const response = await runWithPageTopLoader(() =>
        fetchWithAuthRetry(`${DOWNLOAD_API_BASE_URL}/download/zip`, {
          method: "POST",
          headers: authHeaders,
          body: JSON.stringify(payload),
        })
      );

      const data = await response.json().catch(() => ({}));
      const remainingDownloadCount = Number(data?.remainingDownloadCount);
      const nextRemaining = Number.isFinite(remainingDownloadCount)
        ? remainingDownloadCount
        : (Number(row.remainingDownloadCount) || DEFAULT_DOWNLOAD_LIMIT);

      setTableData((prev) =>
        prev.map((item) => {
          if (extractZipFileName(item) !== fileName) return item;
          return {
            ...item,
            remainingDownloadCount: nextRemaining,
          };
        })
      );

      if (response.status === 429 || response.status === 403) {
        window.alert("このZIPファイルはダウンロード上限（10回）に達しています。");
        return;
      }

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error("認証トークンの更新に失敗しました。再ログインしてください。");
        }
        throw new Error(data?.message || `ZIP API failed: ${response.status}`);
      }

      if (!data?.signedUrl) {
        throw new Error("signedUrl is missing from API response");
      }

      handleDownload(data.signedUrl);
    } catch (error) {
      console.error("ZIP download tracking failed:", error);
      window.alert("ダウンロード処理に失敗しました。時間をおいて再度お試しください。");
    }
  };

  const handleCsvDownload = async (row) => {
    if (!row) return;

    try {
      if (!DOWNLOAD_API_BASE_URL) {
        throw new Error("REACT_APP_API_BASE_URL is not configured");
      }

      const fileName = extractZipFileName(row);
      const payload = {
        fileName,
        fileType: "csv",
        email: localStorage.getItem("email") || "",
        userId: localStorage.getItem("username") || "",
        userName: localStorage.getItem("username") || "",
      };
      const authHeaders = await getFreshAuthHeaders();

      const response = await runWithPageTopLoader(() =>
        fetchWithAuthRetry(`${DOWNLOAD_API_BASE_URL}/download/zip`, {
          method: "POST",
          headers: authHeaders,
          body: JSON.stringify(payload),
        })
      );

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        if (response.status === 401) {
          throw new Error("認証トークンの更新に失敗しました。再ログインしてください。");
        }
        throw new Error(data?.message || `CSV API failed: ${response.status}`);
      }
      if (!data?.signedUrl) {
        throw new Error("signedUrl is missing from API response");
      }

      handleDownload(data.signedUrl);
    } catch (error) {
      console.error("CSV download failed:", error);
      window.alert("CSVダウンロードに失敗しました。時間をおいて再度お試しください。");
    }
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    setCurrentPageNumber(value);
  };

  // ---- フィルター適用ロジック ----
  const selectedAreas = normalizeSelectedAreas(
    filterDownload &&
      filterDownload.areaGroup &&
      filterDownload.areaGroup.value
  );

  const dateRange = filterDownload && filterDownload.dateRange;
  const hasDateRange = dateRange && dateRange.start && dateRange.end;
  const fileNameKeywords =
    filterDownload && Array.isArray(filterDownload.fileNameKeywords)
      ? filterDownload.fileNameKeywords
          .map((keyword) =>
            typeof keyword === "string" ? keyword.trim().toLowerCase() : ""
          )
          .filter(Boolean)
      : [];
  const hasFileNameKeywords = fileNameKeywords.length > 0;

  // 1) 県域フィルタ
  const areaFiltered =
    selectedAreas.length === 0 || selectedAreas.includes("all")
      ? tableData
      : tableData.filter((item) =>
          selectedAreas.includes(normalizeAreaValue(item.area))
        );

  // 2) 日付レンジフィルタ（文字列 YYYY-MM-DD 同士なので比較OK）
  const dateFiltered = hasDateRange
    ? areaFiltered.filter((item) => {
        if (!item.date) return false;
        return item.date >= dateRange.start && item.date <= dateRange.end;
      })
    : areaFiltered;

  // 3) ファイル名キーワード（OR 条件の部分一致）
  const filteredData = hasFileNameKeywords
    ? dateFiltered.filter((item) => {
        const fileName = String(item.fileName || "").toLowerCase();
        return fileNameKeywords.some((keyword) => fileName.includes(keyword));
      })
    : dateFiltered;

  // ページングは「絞り込まれた件数」を基準にする
  const totalFiltered = filteredData.length;
  const pageCount = Math.ceil(totalFiltered / itemsPerPage) || 1;

  useEffect(() => {
    if (typeof onFilteredCountChange === "function") {
      onFilteredCountChange(totalFiltered);
    }
  }, [totalFiltered, onFilteredCountChange]);

  // 現在ページに表示するデータだけを取り出す
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = filteredData.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  return (
    <>
      <Grid
        container
        direction="column"
        justifyContent={"center"}
        className={open ? classes.tableWrapperSlim : classes.tableWrapper}
      >
        <Grid item>
          <div className={classes.tableBlock}>
            <div className={classes.tableBlockHeader}>
              <span /> {/* 左側を空にして右寄せ維持（不要なら消してOK） */}
            </div>
            <TableContainer component={Paper}>
              <Table stickyHeader className={classes.table}>
                <TableHead className={classes.tableHead}>
                  <TableRow>
                    <TableCell align="center">#</TableCell>
                    <TableCell align="center">ファイル名</TableCell>
                    <TableCell align="center">作成日時</TableCell>
                    <TableCell align="center">画像ダウンロード</TableCell>
                    <TableCell align="center">CSVダウンロード</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.length > 0 &&
                    paginatedData.map((row, index) => (
                      <TableRow
                        className={classes.tableRow}
                        key={row.id || index}
                      >
                        <TableCell
                          component="th"
                          scope="row"
                          style={{ width: "10%" }}
                          align="center"
                        >
                          {startIndex + index + 1}
                        </TableCell>

                        <TableCell style={{ width: "30%" }} align="center">
                          <span>{row.fileName}</span>
                          {(Number.isFinite(row.reportCount) ||
                            Number.isFinite(row.imageCount)) && (
                            <span className={classes.fileMeta}>
                              （報告
                              {Number.isFinite(row.reportCount)
                                ? row.reportCount
                                : "-"}
                              件 / 画像
                              {Number.isFinite(row.imageCount)
                                ? row.imageCount
                                : "-"}
                              枚）
                            </span>
                          )}
                        </TableCell>

                        <TableCell style={{ width: "20%" }} align="center">
                          {row.date}
                        </TableCell>
                        <TableCell style={{ width: "20%" }} align="center">
                          <button
                            type="button"
                            onClick={() => handleZipDownload(row)}
                            disabled={(row.remainingDownloadCount || 0) <= 0}
                            style={{
                              cursor: "pointer",
                              color: "blue",
                              textDecoration: "underline",
                              background: "none",
                              border: "none",
                              padding: 0,
                              opacity:
                                (row.remainingDownloadCount || 0) <= 0 ? 0.5 : 1,
                            }}
                          >
                            画像ダウンロード (zip)
                          </button>
                          <br />
                          <span className={classes.fileMeta}>
                            DL残り回数:{" "}
                            {Number.isFinite(row.remainingDownloadCount)
                              ? row.remainingDownloadCount
                              : DEFAULT_DOWNLOAD_LIMIT}
                            回
                          </span>
                        </TableCell>
                        <TableCell style={{ width: "20%" }} align="center">
                          <button
                            type="button"
                            onClick={() => handleCsvDownload(row)}
                            style={{
                              cursor: "pointer",
                              color: "blue",
                              textDecoration: "underline",
                              background: "none",
                              border: "none",
                              padding: 0,
                            }}
                          >
                            CSVダウンロード
                          </button>
                        </TableCell>
                      </TableRow>
                    ))}

                  {paginatedData.length === 0 && (
                    <TableRow className={classes.tableRowNone}>
                      <TableCell colSpan={5} className={classes.noDataCell}>
                        データがありません
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </Grid>

        {/* 件数が 0 のときはページネーション非表示 */}
        {totalFiltered > 0 && (
          <Grid item>
            <div className={classes.pagination}>
              <Pagination
                count={pageCount}
                page={currentPage}
                onChange={handlePageChange}
                color="secondary"
              />
            </div>
          </Grid>
        )}
      </Grid>
    </>
  );
};

DownloadTable.propTypes = {
  currentPageNumber: PropTypes.number,
  setCurrentPageNumber: PropTypes.func.isRequired,
  open: PropTypes.bool,
  setTotalFiles: PropTypes.func.isRequired,
  setAreaGroup: PropTypes.func.isRequired,
  setPageTopLoader: PropTypes.func.isRequired,
  filterDownload: PropTypes.object,
  onFileNameOptionsChange: PropTypes.func,
  onFilteredCountChange: PropTypes.func,
};

const mapStateToProps = (state) => ({
  currentPageNumber: state.home.currentPageNumber,
  filterDownload: state.download.filterDownload,
});

const mapDispatchToProps = (dispatch) => ({
  setTotalFiles: (param) => dispatch(setTotalFiles(param)),
  setAreaGroup: (param) => dispatch(setAreaGroup(param)),
  setCurrentPageNumber: (param) => dispatch(setCurrentPageNumber(param)),
  setPageTopLoader: (param) => dispatch(setPageTopLoader(param)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(null, { withTheme: true })(DownloadTable));
